/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Curve from "./curve";
import {BoundingBox, EEmbededElementTypes, RenderElement, RenderDocument} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import AbstractElement from "./abstract_element";
import Factory from "./factory";

export default
class GeoPath extends Curve {

    public static TypeName: string = EEmbededElementTypes.GEOPATH;
    private _inputPositions: number[];
    private _isInside: boolean;
    private _nofInputs: number;
    private _refinements: ({ indices: any[]; nofRefinements: number; step: number; positions: any[] })[];
    private _coarserRenderElementsToRemove: RenderElement[];
    private _elevationComputed: boolean[];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._inputPositions = [];
        this._isInside = false;
        this._nofInputs = -1;

        this._refinements = [
            {
                "step": 100,
                "nofRefinements": 0,
                "positions": [],
                "indices": []
            },
            {
                "step": 10,
                "nofRefinements": 0,
                "positions": [],
                "indices": []
            },
            {
                "step": 5,
                "nofRefinements": 0,
                "positions": [],
                "indices": []
            },
            {
                "step": 3,
                "nofRefinements": 0,
                "positions": [],
                "indices": []
            },
            {
                "step": 1,
                "nofRefinements": 0,
                "positions": [],
                "indices": []
            }
        ];

        this._coarserRenderElementsToRemove = [];
    }

    public static create(aRenderDocument: RenderDocument): GeoPath {
        let element = new GeoPath(aRenderDocument);
        element._typeName = GeoPath.TypeName;
        return element;
    }

    /***
     * Covert the 2d gps line to 3d line. Take each 10th point and comute y value for it. The rest points will be progressively computed
     * during rendering.
     * @param aEPSGCode
     * @param a2DPointsArray
     */
    public setPositionsArray(a2DPointsArray: number[][], aEPSGCode?: string) {
        this._positions = [];
        this._elevationComputed = [];
        this._indices = [];

        this._nofInputs = a2DPointsArray.length;

        let index = 0;
        let position;
        for (let i = 0; i < a2DPointsArray.length; i++) {
            position = this._sceneAPI.getDocument().transformCoordSystemToLocal(aEPSGCode, a2DPointsArray[i]);
            if (i % this._refinements[0].step === 0) {
                position[1] = this._sceneAPI.pickingModule._getYValue(vec2.fromValues(position[0], position[2])) + 10.0;

                this._refinements[0].nofRefinements += this._refinements[0].step;

                this._refinements[0].positions.push(position[0]);
                this._refinements[0].positions.push(position[1]);
                this._refinements[0].positions.push(position[2]);

                this._refinements[0].indices.push(index++);
                this._refinements[0].indices.push(index);

                this._elevationComputed.push(true);
            } else {
                this._elevationComputed.push(false);
            }

            this._inputPositions.push(position[0]);
            this._inputPositions.push(position[1]);
            this._inputPositions.push(position[2]);
        }

        this._positions = this._refinements[0].positions;
        this._indices = this._refinements[0].indices;

        this._isInside = true;
    }

    public needsRefine(): boolean {
        for (let i = 0; i < this._refinements.length; i++) {
            if (this._refinements[i].nofRefinements < this._nofInputs) {
                return true;
            }
        }

        return false;
    }

    public refine(aSteps: number) {
        if (this._coarserRenderElementsToRemove.length > 0) {
            if (this._renderDocumentProperties.renderElement && this._renderDocumentProperties.renderElement.getIsReadyToRender()) {
                for (let i = 0; i < this._coarserRenderElementsToRemove.length; i++) {
                    this._renderDocumentProperties.renderModel.removeElement(this._coarserRenderElementsToRemove[i]);
                }
                this._coarserRenderElementsToRemove = [];
            }
        }

        if (!this.needsRefine()) {
            return;
        }

        let position;
        let startTime = performance.now();
        for (let i = 0; i < this._refinements.length; i++) {
            if (this._refinements[i].nofRefinements < this._nofInputs) {
                let index = Math.floor(this._refinements[i].indices.length / 2);
                for (let r = this._refinements[i].nofRefinements; r < this._nofInputs; r++) {
                    if (r % this._refinements[i].step === 0) {
                        position = [
                            this._inputPositions[this._refinements[i].nofRefinements * 3 + 0],
                            this._inputPositions[this._refinements[i].nofRefinements * 3 + 1],
                            this._inputPositions[this._refinements[i].nofRefinements * 3 + 2]
                        ];
                        if (!this._elevationComputed[r]) {
                            position[1] = this._sceneAPI.pickingModule._getYValue(vec2.fromValues(position[0], position[2])) + 10.0;
                            this._elevationComputed[r] = true;
                            this._inputPositions[this._refinements[i].nofRefinements * 3 + 1] = position[1];
                        }

                        this._refinements[i].nofRefinements += this._refinements[i].step;

                        this._refinements[i].positions.push(position[0]);
                        this._refinements[i].positions.push(position[1]);
                        this._refinements[i].positions.push(position[2]);

                        this._refinements[i].indices.push(index++);
                        this._refinements[i].indices.push(index);

                        aSteps--;
                        if (aSteps <= 0) {
                            return (performance.now() - startTime);
                        }
                    }
                }

                if (this._refinements[i].nofRefinements >= this._nofInputs) {
                    this._positions = this._refinements[i].positions;
                    this._indices = this._refinements[i].indices;
                    this._coarserRenderElementsToRemove.push(this._renderDocumentProperties.renderElement);
                    this._renderDocumentProperties.renderElement = null;
                    this.draw(true);
                }
            }
        }

        return (performance.now() - startTime);
    }

    /***
     * Return start position of the path
     */
    public getPosition(): number[] {
        if (this._positions.length < 3) {
            return null;
        }

        return [this._positions[0], this._positions[1], this._positions[2]];
    }

    public isInsideScene(): boolean {
        return this._isInside;
    }

    public remove(): boolean {
        this._sceneAPI.getGeoPathsManager().remove(this);

        AbstractElement.prototype.remove.call(this);

        return true;
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateGeometryObjectDescriptionJSON(): any {
        // compute bbox from positions
        this._bbox = new BoundingBox();
        for (let i = 0; i < this._positions.length; i += 3) {
            this._bbox.extendByXYZ(this._positions[i + 0], this._positions[i + 1], this._positions[i + 2]);
        }

        let min = vec3.clone(this._bbox.getMin());
        let max = vec3.clone(this._bbox.getMax());

        return [{
            description: {
                type: "LINE",
                bboxMin: [min[0], min[1], min[2]],
                bboxMax: [max[0], max[1], max[2]]
            },
            appearance: {
                material: {
                    diffuseColor: this._properties.surfaceColor.diffuseColor,
                    ambientColor: this._properties.surfaceColor.ambientColor,
                    emissiveColor: this._properties.surfaceColor.emissiveColor,
                    specularColor: this._properties.surfaceColor.specularColor,
                    shininess: this._properties.surfaceColor.shininess,
                    transparency: this._properties.surfaceColor.transparency
                },
                lineProperties: {
                    linetype: this._properties.lineProperties.linetype,
                    linewidthScaleFactor: this._properties.lineProperties.linewidthScaleFactor + 5.0
                },
                renderOption: {
                    polygonOffsetFactor: this._properties.renderOption.polygonOffsetFactor,
                    polygonOffsetUnits: this._properties.renderOption.polygonOffsetUnits
                }
            },
            indexedLineSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: this._positions.length / 3,
                indexCount: this._indices.length,
                binSize: 0,
                positions: this._positions,
                indices: this._indices
            }
        }];
    }
}

Factory.register(GeoPath);
